import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Heart, ArrowRight, ArrowLeft, Sparkles, Loader2 } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const QuizPage = () => {
  const navigate = useNavigate();
  const { token, updateUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/quiz/questions`);
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error("Unable to load quiz. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await axios.post(
        `${API}/quiz/submit`,
        { answers: formattedAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      updateUser({ quiz_completed: true, current_state: response.data.state });
    } catch (error) {
      toast.error("Unable to submit quiz. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;
  const currentQ = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const canProceed = currentQ && answers[currentQ.id];

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center" data-testid="quiz-loading">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  // Show result
  if (result) {
    return (
      <div className="min-h-screen hero-gradient px-6 py-12" data-testid="quiz-result">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6 animate-breathe">
              <Sparkles className="w-10 h-10 text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Your State of Being
            </h1>
            <p className="text-muted-foreground text-lg">
              Beautiful one, here is what your soul is telling us
            </p>
          </div>

          {/* Result Card */}
          <Card className="glass-card rounded-3xl border-0 mb-8">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <span className="inline-block bg-primary/10 text-primary px-6 py-2 rounded-full font-medium mb-4">
                  {result.state}
                </span>
                <p className="text-lg text-foreground leading-relaxed">
                  {result.description}
                </p>
              </div>

              {/* Morning Mantra */}
              <div className="bg-muted/50 rounded-2xl p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-lg">☀️</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Your Morning Mantra</h3>
                </div>
                <p className="font-heading text-lg text-foreground italic leading-relaxed" data-testid="morning-mantra">
                  "{result.morning_mantra}"
                </p>
              </div>

              {/* Evening Blessing */}
              <div className="bg-accent/10 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
                    <span className="text-lg">🌙</span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold">Your Evening Blessing</h3>
                </div>
                <p className="font-heading text-lg text-foreground italic leading-relaxed" data-testid="evening-blessing">
                  "{result.evening_blessing}"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Button
              onClick={() => navigate("/dashboard")}
              className="btn-primary text-lg px-10 py-6"
              data-testid="continue-to-dashboard-btn"
            >
              Continue to Your Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient px-6 py-8" data-testid="quiz-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Heart className="w-7 h-7 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-2">
            State of Being Quiz
          </h1>
          <p className="text-muted-foreground">
            Answer honestly, beautiful one. There are no wrong answers.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2 bg-muted" />
        </div>

        {/* Question Card */}
        {currentQ && (
          <Card className="glass-card rounded-3xl border-0 mb-8">
            <CardContent className="p-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground mb-8 leading-relaxed" data-testid="quiz-question">
                {currentQ.question}
              </h2>

              <div className="space-y-4">
                {currentQ.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQ.id, option.value)}
                    className={`quiz-option w-full text-left ${
                      answers[currentQ.id] === option.value ? "selected" : ""
                    }`}
                    data-testid={`quiz-option-${option.value}`}
                  >
                    <span className="text-foreground">{option.label}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className="text-muted-foreground hover:text-foreground"
            data-testid="quiz-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed || submitting}
              className="btn-primary"
              data-testid="quiz-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Discover My State
                  <Sparkles className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="btn-primary"
              data-testid="quiz-next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPage;
